import React, { useState, useEffect } from "react";
import {
  Modal,
  Box,
  Typography,
  TextField,
  Button,
  Fade,
  styled,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  FormHelperText,
} from "@mui/material";
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import * as Yup from "yup";

const requirementsForPass = [
  { label: "At least one capital letter (A-Z)", regex: /[A-Z]/ },
  { label: "At least one lowercase letter (a-z)", regex: /[a-z]/ },
  { label: "At least one special character", regex: /[!@#$%^&*]/ },
];

const requirementsForConfirmPass = [
  { label: "At least one number (0-9)", regex: /[0-9]/ },
  { label: "Minimum length is 8 characters", regex: /.{8,}/ },
];


const ModalBoxContainer = styled(Box)(({ theme }) => ({
  top: "50%",
  position: "absolute",
  left: "50%",
  backgroundColor: theme.palette.background.paper,
  transform: "translate(-50%, -50%)",
  padding: theme.spacing(3),
  boxShadow: theme.shadows[5],
  borderRadius: '20px',
  width: "500px",
  outline: "none",
  '@media (min-width: 320px) and (max-width: 530px)': {
    width: "auto",
  },
  "& .titleText": {
    color: "#000000",
    fontWeight: 500,
    fontSize: "20px",
    marginBottom: "20px",
  },
  "& .fieldLabelText": {
    fontWeight: 400,
    fontSize: "14px",
    margin: "15px 0px 15px 0px",
    color: '#0F172A',
    lineHeight: '22px'
  },
  '& .input': {
    lineHeight: '24px',
    fontSize: '16px',
    fontWeight: '400',
    color: '#64748b !important',
    "& .MuiOutlinedInput-root": {
      borderRadius: "20px",
    },
    '@media (max-width: 414px)': {
      fontSize: '12px',
      lineHeight: '20px',
    },
    '@media (min-width: 415px) and (max-width: 1280px)': {
      fontSize: '14px',
      lineHeight: '22px',
    },
    '@media (min-width: 1600px)': {
      fontSize: '18px',
      lineHeight: '26px',
    },
  },
  '& input::placeholder': {
    lineHeight: '24px',
    opacity: '1 !important',
    fontSize: '16px',
    color: '#64748b !important',
    "& .MuiOutlinedInput-root": {
      borderRadius: "20px",
    },
  },
  "& .passwordBox": {
    display: 'flex',
    gap: '15px'
  },
  '& .css-1wc848c-MuiFormHelperText-root.Mui-error, & .errorText, & .validationsChecks, & .css-1wc848c-MuiFormHelperText-root.Mui-error': {
    lineHeight: '22px !important',
    color: '#E11B23 !important',
    fontSize: '12px !important',
  },
  '& .verifyText': {
    color: '#E11B23 !important',
  },
  '& .validationsChecks': {
    gap: '6px',
    color: '#0F172A !important',
    padding: '0',
    '& div': {
      minWidth: 'auto',
    },
    '& svg': {
      width: '14px',
      height: '14px',
    }
  },
  "& .btnContainerBox": {
    display: "flex",
    justifyContent: "end",
    marginTop: "20px",
    gap: "15px",
  },
  "& .saveBottons": {
    fontWeight: "600",
    width: "140px !important",
    fontSize: "14px",
    color: "#fff !important",
    textTransform: "none",
    height: "56px",
    background: "linear-gradient(90deg, #8077FF 0%, #652784 60%)",
    borderRadius: '32px',
    transition: "opacity 0.3s",
    opacity: 1,
    "&:disabled": {
      pointerEvents: "none",
      opacity: 0.5,
    },
  },
  "& .textField":{
    fontSize: '16px',
		lineHeight: '24px',
		color: '#64748b !important',
		opacity: '1 !important',
		"& .MuiOutlinedInput-root": {
			borderRadius: "50px",
			borderColor: "#e2e8f0",
			"&:hover": { borderColor: "#cbd5e1" },
			"&.Mui-focused": {
				borderColor: "#673ab7",
				boxShadow: "0 0 0 3px rgba(103, 58, 183, 0.2)",
			},
		},
  },
  "& .cancelBottons": {
    fontSize: "14px",
    width: "140px !important",
    fontWeight: "600",
    color: "#652784 !important",
    height: "56px",
    borderRadius: '32px',
    borderColor: "#652784",
    textTransform: "none",
  },
}));

interface ErrorState {
  oldPassword: string;
  newPassword: string;
  confirmPassword: string;
  errorMessage?:string;
}

const ResetPassword: React.FC<any> = ({
  open,
  onClose,
  handleOnClick,
  errorMessage
}) => {
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState({
    old: false,
    new: false,
    confirm: false,
  });

  const [errors, setErrors] = useState<ErrorState>({
    oldPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const [touched, setTouched] = useState({
    oldPassword: false,
    newPassword: false,
    confirmPassword: false,
  });

  const validationSchema = Yup.object().shape({
    oldPassword: Yup.string().required("Old Password is required!"),
    newPassword: Yup.string()
      .required("New Password is required!"),
    confirmPassword: Yup.string()
      .required("Confirm Password is required!")
      .oneOf([Yup.ref("newPassword"), null], "Passwords must match")
  });

  const validateForm = async () => {
    try {
      await validationSchema.validate({
        oldPassword,
        newPassword,
        confirmPassword,
      }, { abortEarly: false });

      setErrors({ oldPassword: "", newPassword: "", confirmPassword: "" });
      return true;
    } catch (err: any) {
      const newErrors: ErrorState = { oldPassword: "", newPassword: "", confirmPassword: "" };
      err.inner.forEach((e: any) => {
        newErrors[e.path as keyof ErrorState] = e.message;
      });
      setErrors(newErrors);
      return false;
    }
  };

  useEffect(() => {
    if (touched.oldPassword || touched.newPassword || touched.confirmPassword) {
      validateForm();
    }
  }, [oldPassword, newPassword, confirmPassword, touched]);

  const handleSave = async () => {
    const isValid = await validateForm();
    if (isValid) {
      handleOnClick(newPassword, confirmPassword, oldPassword);
    }
  };

  const handleClose = () => {
    setOldPassword("");
    setNewPassword("");
    setConfirmPassword("");
    setErrors({ oldPassword: "", newPassword: "", confirmPassword: "" });
    setTouched({ oldPassword: false, newPassword: false, confirmPassword: false });
    onClose();
  };

  useEffect(() => {
    setOldPassword("");
    setNewPassword("");
    setConfirmPassword("");
    setErrors({ oldPassword: "", newPassword: "", confirmPassword: "" });
    setTouched({ oldPassword: false, newPassword: false, confirmPassword: false });
  }, [open])
  

  const togglePasswordVisibility = (field: 'old' | 'new' | 'confirm') => {
    setShowPassword(prev => ({ ...prev, [field]: !prev[field] }));
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>, field: 'oldPassword' | 'newPassword' | 'confirmPassword') => {
    const { value } = e.target;
    if (field === 'oldPassword') {
      setOldPassword(value);
    } else if (field === 'newPassword') {
      setNewPassword(value);
    } else if (field === 'confirmPassword') {
      setConfirmPassword(value);
    }
    setTouched(prev => ({ ...prev, [field]: true }));
  };

  const isPasswordValid = newPassword && requirementsForPass.every(req => req.regex.test(newPassword)) &&
    requirementsForConfirmPass.every(req => req.regex.test(newPassword)) &&
    newPassword.length >= 8;

  const isFormValid = isPasswordValid && newPassword === confirmPassword;

  return (
    <Modal open={open} onClose={onClose} closeAfterTransition>
      <Fade in={open}>
        <ModalBoxContainer>
          <Typography className="titleText">Reset Password</Typography>
          <Divider />
          <Typography className="fieldLabelText">Old Password</Typography>
          <TextField
            fullWidth
            variant="outlined"
            name="old-password"
            autoComplete="new-password"
            placeholder="Enter your old password"
            type={showPassword.old ? "text" : "password"}
            value={oldPassword}
            onChange={(e) => handleInputChange(e as React.ChangeEvent<HTMLInputElement>, 'oldPassword')}  // Cast the event type
            className="textField"
            error={touched.oldPassword && !!errors.oldPassword}
            helperText={touched.oldPassword && errors.oldPassword}
            InputProps={{
              endAdornment: (
                <IconButton onClick={() => togglePasswordVisibility('old')}>
                  {showPassword.old ? (
                    <VisibilityOutlinedIcon sx={{ fill: "#94A3B8" }} />
                  ) : (
                    <VisibilityOffOutlinedIcon sx={{ fill: "#94A3B8" }} />
                  )}
                </IconButton>
              ),
            }}
          />
          <Typography className="fieldLabelText">New Password</Typography>
          <TextField
            fullWidth
            variant="outlined"
            name="new-password"
            autoComplete="new-password"
            placeholder="Enter your new password"
            type={showPassword.new ? "text" : "password"}
            value={newPassword}
            className="textField"
            onChange={(e) => handleInputChange(e as React.ChangeEvent<HTMLInputElement>, 'newPassword')}
            error={touched.newPassword && !!errors.newPassword}
            helperText={touched.newPassword && errors.newPassword}
            InputProps={{
              endAdornment: (
                <IconButton onClick={() => togglePasswordVisibility('new')}>
                  {showPassword.new ? (
                    <VisibilityOutlinedIcon sx={{ fill: "#94A3B8" }} />
                  ) : (
                    <VisibilityOffOutlinedIcon sx={{ fill: "#94A3B8" }} />
                  )}
                </IconButton>
              ),
            }}
          />
          <Typography className="fieldLabelText">Confirm New Password</Typography>
          <TextField
            fullWidth
            variant="outlined"
            name="confirm-new-password"
            autoComplete="new-password"
            placeholder="Confirm your new password"
            type={showPassword.confirm ? "text" : "password"}
            value={confirmPassword}
            className="textField"
            onChange={(e) => handleInputChange(e as React.ChangeEvent<HTMLInputElement>, 'confirmPassword')}
            error={touched.confirmPassword && !!errors.confirmPassword}
            helperText={touched.confirmPassword && errors.confirmPassword}
            InputProps={{
              endAdornment: (
                <IconButton onClick={() => togglePasswordVisibility('confirm')}>
                  {showPassword.confirm ? (
                    <VisibilityOutlinedIcon sx={{ fill: "#94A3B8" }} />
                  ) : (
                    <VisibilityOffOutlinedIcon sx={{ fill: "#94A3B8" }} />
                  )}
                </IconButton>
              ),
            }}
          />
          <Box className="passwordBox">
            {newPassword && (
              <List>
                {requirementsForPass.map((req, index) => {
                  const isValid = req.regex.test(newPassword);
                  return (
                    <ListItem key={index} className="validationsChecks" sx={{ marginTop: '5px' }}>
                      <ListItemIcon>
                        <InfoOutlinedIcon sx={{ fill: isValid ? '#409046' : '#E11B23' }} />
                      </ListItemIcon>
                      {req.label}
                    </ListItem>
                  );
                })}
              </List>
            )}
            {newPassword && (
              <List>
                {requirementsForConfirmPass.map((req, index) => {
                  const isValid = req.regex.test(newPassword);
                  return (
                    <ListItem key={index} className="validationsChecks" sx={{ marginTop: '5px' }}>
                      <ListItemIcon>
                        <InfoOutlinedIcon sx={{ fill: isValid ? '#409046' : '#E11B23' }} />
                      </ListItemIcon>
                      {req.label}
                    </ListItem>
                  );
                })}
              </List>
            )}
          </Box>
          {errorMessage && (
            <FormHelperText error style={{ marginTop: 4 }}>
              {errorMessage}
            </FormHelperText>
          )}
          <Box className="btnContainerBox">
            <Button
              className="saveBottons"
              variant="contained"
              onClick={handleSave}
              disabled={!isFormValid}
            >
              Reset
            </Button>
            <Button className="cancelBottons" variant="outlined" onClick={handleClose}>
              Cancel
            </Button>
          </Box>
        </ModalBoxContainer>
      </Fade>
    </Modal>
  );
};

export default ResetPassword;
